import { useEffect, useId, useRef, useState, type ReactNode } from "react"
import { ChevronDownIcon } from "./icons"

export interface SplitActionItem {
  label: string
  onClick: () => void
  icon?: ReactNode
}

export function SplitActionButton({
  label,
  icon,
  onClick,
  items,
  disabled = false,
  primaryDisabled = false,
  variant = "default",
}: {
  label: string
  icon?: ReactNode
  onClick: () => void
  items: SplitActionItem[]
  disabled?: boolean
  primaryDisabled?: boolean
  variant?: "default" | "primary"
}) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuId = useId()

  useEffect(() => {
    if (!open) return

    const handleOutsidePointerDown = (event: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }

    document.addEventListener("pointerdown", handleOutsidePointerDown)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("pointerdown", handleOutsidePointerDown)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [open])

  const handlePrimaryClick = () => {
    setOpen(false)
    onClick()
  }

  return (
    <div ref={containerRef} className="relative inline-flex w-full sm:w-auto">
      <button
        type="button"
        onClick={handlePrimaryClick}
        disabled={disabled || primaryDisabled}
        className={`inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-l-full border px-4 py-2 text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 sm:flex-none ${
          variant === "primary"
            ? "border-forest-950 bg-forest-950 text-white hover:bg-forest-900"
            : "border-ink/20 bg-white text-ink hover:bg-cream-100"
        }`}
      >
        {icon}
        {label}
      </button>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((current) => !current)}
        disabled={disabled}
        aria-label={`${label} actions`}
        aria-controls={menuId}
        aria-expanded={open}
        aria-haspopup="menu"
        className={`inline-flex min-h-11 items-center justify-center rounded-r-full border border-l-0 px-3 transition-colors disabled:pointer-events-none disabled:opacity-50 ${
          variant === "primary"
            ? "border-forest-950 bg-forest-950 text-white hover:bg-forest-900"
            : "border-ink/20 bg-white text-ink hover:bg-cream-100"
        }`}
      >
        <ChevronDownIcon className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={`${label} actions`}
          className="absolute right-0 top-full z-40 mt-2 min-w-52 overflow-hidden rounded-xl border border-gold-500 bg-white p-1.5 shadow-lg"
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                item.onClick()
              }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm text-ink transition-colors hover:bg-cream-100"
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
